import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import maplibregl from 'maplibre-gl';
import { useQuery } from '@apollo/client';
import { useRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import Select from 'react-select';
import {
  booleanPointInPolygon,
  circle as turfCircle,
  polygon as turfPolygon,
  bbox as turfBbox,
  distance as turfDistance,
  area as turfArea,
} from '@turf/turf';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  faUtensils, faShoppingBag, faLandmark, faStar, faHeartbeat,
  faBed, faBuilding, faGraduationCap, faTree, faRunning,
} from '@fortawesome/free-solid-svg-icons';
import { PROPERTIES_GEOJSON, NEIGHBORHOODS_GEOJSON } from '@api';
import { Icon } from '@assets';
import config from '../../../config';
import { buildMapVars } from './locationMapUtils';
import { selectedPropertiesAtom } from '../../../store/atoms/PropertiesAtom';

// Configure MapLibre GL worker
if (typeof window !== 'undefined') {
  try {
    maplibregl.workerUrl = `${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`;
  } catch (err) {
    console.warn('LocationMapPanel: Failed to set maplibre workerUrl:', err);
  }
}

const MAP_STYLE      = `https://api.maptiler.com/maps/openstreetmap/style.json?key=${config.MAPTILER_API_KEY}`;

const EMPTY_GEOJSON  = { type: 'FeatureCollection', features: [] };
const SOURCE_ID      = 'fp-properties';
const LAYER_ID       = 'fp-properties-circles';
const THEME_BLUE     = '#4880ff';
const INITIAL_CENTER = [-96.8, 32.8];
const INITIAL_ZOOM   = 4.5;

// Draw layer IDs
const DRAW_SOURCE_ID    = 'fp-draw';
const DRAW_FILL_ID      = 'fp-draw-fill';
const DRAW_LINE_ID      = 'fp-draw-line';
const DRAW_SOURCE_VERTS = 'fp-draw-verts';
const DRAW_VERT_ID      = 'fp-draw-vertices';

// Rubber-band preview line (polygon mode)
const PREVIEW_SOURCE = 'fp-draw-preview';
const PREVIEW_LAYER  = 'fp-draw-preview-line';

// Pixel distance within which clicking snaps to the first polygon vertex to close the shape
const POLYGON_SNAP_PX = 15;

// Permanent source showing all finalized shapes (separate from DRAW_SOURCE_ID used for in-progress drawing)
const SHAPES_SOURCE_ID  = 'fp-all-shapes';
const SHAPES_FILL_LAYER = 'fp-all-shapes-fill';
const SHAPES_LINE_LAYER = 'fp-all-shapes-line';

// Property marker layers (halo + cluster + individual dot + cluster count text)
const HALO_LAYER_ID    = 'fp-properties-halo';
const CLUSTER_LAYER_ID = 'fp-clusters';
const CLUSTER_COUNT_ID = 'fp-cluster-count';

// Individual (unclustered) marker source + layers — toggled via localStorage preference
const SOURCE_INDIVIDUAL_ID = 'fp-properties-ind';
const IND_HALO_LAYER_ID    = 'fp-properties-halo-ind';
const IND_LAYER_ID         = 'fp-properties-circles-ind';

// Radius label
const RADIUS_LABEL_SOURCE = 'fp-radius-label';
const RADIUS_LABEL_LAYER  = 'fp-radius-label-sym';

// Shortlisted heart marker layers (symbol layers drawn on top of circle layers)
const SHORTLISTED_CLUSTER_LAYER = 'fp-shortlisted-cluster';
const SHORTLISTED_IND_LAYER     = 'fp-shortlisted-ind';

// ── Overlay layer definitions (neighborhoods only — auto-on when city selected) ──
const OVERLAY_CONFIGS = {
  neighborhoods: {
    sourceId: 'fp-nbhd',
    layers: [
      {
        id: 'fp-nbhd-fill', type: 'fill', before: LAYER_ID,
        paint: { 'fill-color': ['coalesce', ['get', 'fill'], THEME_BLUE], 'fill-opacity': 0.08 },
      },
      {
        id: 'fp-nbhd-hover', type: 'fill', before: LAYER_ID,
        paint: { 'fill-color': ['coalesce', ['get', 'fill'], THEME_BLUE], 'fill-opacity': 0.28 },
        filter: ['==', ['get', 'neighborhood_id'], -1],
      },
      {
        id: 'fp-nbhd-line', type: 'line', before: LAYER_ID,
        paint: { 'line-color': '#1a1a1a', 'line-width': 2.5, 'line-opacity': 0.85 },
      },
      {
        id: 'fp-nbhd-label', type: 'symbol',
        layout: {
          'text-field': ['upcase', ['coalesce', ['get', 'title'], ['get', 'name'], ['get', 'NAME'], '']],
          'text-size': 13,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'symbol-sort-key': ['*', -1, ['coalesce', ['get', 'area'], 0]],
        },
        paint: { 'text-color': '#1a1a1a', 'text-halo-color': '#ffffff', 'text-halo-width': 2.5 },
      },
    ],
  },
};

// ── Breakpoint detection (isMobile <768px, isTablet 768–1199px) ──────────────
function useTouchBreakpoint() {
  const read = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return { isMobile: false, isTablet: false };
    }
    return {
      isMobile: window.matchMedia('(max-width: 767.98px)').matches,
      isTablet: window.matchMedia('(min-width: 768px) and (max-width: 1199.98px)').matches,
    };
  };
  const [bp, setBp] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const phone  = window.matchMedia('(max-width: 767.98px)');
    const tablet = window.matchMedia('(min-width: 768px) and (max-width: 1199.98px)');
    const handle = () => setBp(read());
    const add = (mq) => mq.addEventListener ? mq.addEventListener('change', handle) : mq.addListener(handle);
    const rem = (mq) => mq.removeEventListener ? mq.removeEventListener('change', handle) : mq.removeListener(handle);
    add(phone); add(tablet);
    return () => { rem(phone); rem(tablet); };
  }, []);
  return { ...bp, isTouch: bp.isMobile || bp.isTablet };
}

// ── Draw modes ────────────────────────────────────────────────────────────────
const DRAW_MODES = [
  { key: 'freehand',   icon: 'pencil-alt',    label: 'Freehand draw' },
  { key: 'radius',     icon: 'circle-notch',  label: 'Radius draw' },
  { key: 'polygon',    icon: 'draw-polygon',  label: 'Polygon draw' },
  { key: 'rectangle',  icon: 'vector-square', label: 'Rectangle draw' },
];

// ── POI toggles ──────────────────────────────────────────────────────────────
// faIcon: FA5 IconDefinition — its SVG path is rasterised onto the pin canvas.
// classes: OpenMapTiles POI `class` values that belong to this category.
const POI_MIN_ZOOM         = 13;
const POI_STORAGE_KEY      = 'smartapt:fastProperties:poiState';

const POI_META = [
  { key: 'food',          icon: 'utensils',       label: 'Food',          color: '#ef4444', clickable: true,  faIcon: faUtensils,
    classes: ['restaurant', 'fast_food', 'cafe', 'bar', 'pub', 'biergarten', 'ice_cream', 'food_court'] },
  { key: 'shop',          icon: 'shopping-bag',   label: 'Shop',          color: '#f59e0b', clickable: true,  faIcon: faShoppingBag,
    classes: ['supermarket', 'department_store', 'mall', 'convenience', 'clothes', 'gift', 'furniture',
              'electronics', 'bookshop', 'alcohol', 'shoe_shop', 'kiosk', 'general', 'marketplace'] },
  { key: 'cultural',      icon: 'landmark',       label: 'Cultural',      color: '#8b5cf6', clickable: true,  faIcon: faLandmark,
    classes: ['museum', 'theatre', 'cinema', 'arts_centre', 'gallery', 'library'] },
  { key: 'attraction',    icon: 'star',           label: 'Attraction',    color: '#ec4899', clickable: true,  faIcon: faStar,
    classes: ['attraction', 'viewpoint', 'zoo', 'theme_park', 'castle', 'monument', 'ruins'] },
  { key: 'health',        icon: 'heartbeat',      label: 'Health',        color: '#10b981', clickable: false, faIcon: faHeartbeat,
    classes: ['hospital', 'pharmacy', 'clinic', 'doctors', 'dentist', 'veterinary'] },
  { key: 'accommodation', icon: 'bed',            label: 'Accommodation', color: '#f97316', clickable: true,  faIcon: faBed,
    classes: ['hotel', 'hostel', 'motel', 'guest_house', 'chalet', 'apartment'] },
  { key: 'public',        icon: 'building',       label: 'Public',        color: '#6b7280', clickable: false, faIcon: faBuilding,
    classes: ['town_hall', 'post', 'police', 'fire_station', 'courthouse', 'prison', 'embassy'] },
  { key: 'education',     icon: 'graduation-cap', label: 'Education',     color: '#3b82f6', clickable: false, faIcon: faGraduationCap,
    classes: ['school', 'university', 'kindergarten', 'college'] },
  { key: 'outdoor',       icon: 'tree',           label: 'Outdoor',       color: '#16a34a', clickable: false, faIcon: faTree,
    classes: ['park', 'garden', 'playground', 'camping_site', 'picnic_site', 'nature_reserve'] },
  { key: 'sport',         icon: 'running',        label: 'Sport',         color: '#0ea5e9', clickable: false, faIcon: faRunning,
    classes: ['stadium', 'sports_centre', 'pitch', 'swimming_pool', 'gym', 'sports_hall'] },
];

const POI_DEFAULT_STATE = POI_META.reduce((acc, m) => { acc[m.key] = false; return acc; }, {});

// ── Nearest-POI overlay (dashed connectors from property → nearest POI) ──────
const NEAREST_SOURCE_ID = 'fp-nearest-poi';
const NEAREST_LINE_ID   = 'fp-nearest-poi-line';
const NEAREST_MAX_KM    = 2;   // 1.2 mi cap

// ── Overlay helpers ───────────────────────────────────────────────────────────
function addOverlay(map, key, data) {
  const cfg = OVERLAY_CONFIGS[key];
  if (!cfg || map.getSource(cfg.sourceId)) return;
  map.addSource(cfg.sourceId, { type: 'geojson', data });
  // Nbhd fill/line must sit below any already-inserted POI pin layers so the
  // translucent colored overlay does not mask POI markers. POI layers are
  // added at map-init (before HALO_LAYER_ID) whereas this overlay is added
  // later when a city is selected, so naive `before: LAYER_ID` lands above them.
  const poiAnchor = POI_META
    .map(m => `poi-custom-${m.key}`)
    .find(layerId => map.getLayer(layerId));
  cfg.layers.forEach(({ id, type, paint, layout, before, minzoom, filter }) => {
    if (map.getLayer(id)) return;
    const spec = { id, type, source: cfg.sourceId };
    if (paint)           spec.paint   = paint;
    if (layout)          spec.layout  = layout;
    if (minzoom != null) spec.minzoom = minzoom;
    if (filter)          spec.filter  = filter;
    const effectiveBefore = (type === 'fill' || type === 'line') && poiAnchor
      ? poiAnchor
      : before;
    if (effectiveBefore && map.getLayer(effectiveBefore)) map.addLayer(spec, effectiveBefore);
    else map.addLayer(spec);
  });
}

function removeOverlay(map, key) {
  const cfg = OVERLAY_CONFIGS[key];
  if (!cfg) return;
  [...cfg.layers].reverse().forEach(({ id }) => { if (map.getLayer(id)) map.removeLayer(id); });
  if (map.getSource(cfg.sourceId)) map.removeSource(cfg.sourceId);
}

// ── POI helpers ───────────────────────────────────────────────────────────────

// Parse #rrggbb into "rgba(r,g,b,a)" — used for halo + ring opacity mixing.
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >>  8) & 255;
  const b = (bigint       ) & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Renders a minimal "dot + halo" POI marker on an offscreen canvas at 2×:
// a soft category-color glow behind a thin ring, a solid colored dot, and a
// white FA SVG-path icon centred inside the dot. The icon-anchor for this
// symbol layer is 'center' — the whole shape is symmetric.
function createPinCanvas(color, faIcon) {
  const SCALE = 2, side = 36;
  const w = side * SCALE, h = side * SCALE;
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  const cx = w / 2, cy = h / 2;

  // Soft outer halo — radial gradient falls off to fully transparent
  const grad = ctx.createRadialGradient(cx, cy, side * 0.30, cx, cy, side * 0.95);
  grad.addColorStop(0, hexToRgba(color, 0.28));
  grad.addColorStop(1, hexToRgba(color, 0));
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.arc(cx, cy, side * 0.95, 0, 2 * Math.PI); ctx.fill();

  // Halo ring — thin stroke at mid-radius
  ctx.beginPath();
  ctx.arc(cx, cy, side * 0.72, 0, 2 * Math.PI);
  ctx.strokeStyle = hexToRgba(color, 0.45);
  ctx.lineWidth   = 3;
  ctx.stroke();

  // Colored dot with soft shadow
  ctx.shadowColor   = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur    = 6;
  ctx.shadowOffsetY = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, side * 0.52, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.shadowColor = 'transparent';

  // White rim for legibility on any basemap
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth   = 3;
  ctx.stroke();

  // White FA SVG-path icon, centred
  if (faIcon && faIcon.icon) {
    const [iw, ih, , , pathData] = faIcon.icon;
    const iconBox = side * 0.75;
    const scale   = iconBox / Math.max(iw, ih);
    ctx.save();
    ctx.translate(cx - (iw * scale) / 2, cy - (ih * scale) / 2);
    ctx.scale(scale, scale);
    ctx.fillStyle = '#fff';
    try { ctx.fill(new Path2D(pathData)); } catch (_) {}
    ctx.restore();
  }

  return canvas;
}

// Adds one symbol layer per POI category using custom pin images loaded from
// canvas. Icons are drawn from SVG paths (no font loading needed), and a
// text label (POI name) is rendered below the pin tip.
// onDone() is called after layers are added so click handlers can be registered.
function addCustomPoiLayers(map, onDone) {
  const styleLayers  = map.getStyle()?.layers || [];
  const poiSourceName = styleLayers.find(l => l['source-layer'] === 'poi')?.source;
  if (!poiSourceName) { onDone?.(); return; }

  // Hide the MapTiler OSM style's native POI symbol layers so only our
  // category-colored pins show when a POI is toggled on.
  styleLayers.forEach(l => {
    if (l['source-layer'] !== 'poi') return;
    if (l.id.startsWith('poi-custom-')) return;
    try { map.setLayoutProperty(l.id, 'visibility', 'none'); } catch (_) {}
  });

  POI_META.forEach(({ key, color, classes, faIcon }) => {
    const imgId   = `poi-pin-${key}`;
    const layerId = `poi-custom-${key}`;

    if (!map.hasImage(imgId)) {
      const cv        = createPinCanvas(color, faIcon);
      const imageData = cv.getContext('2d').getImageData(0, 0, cv.width, cv.height);
      map.addImage(imgId, { width: cv.width, height: cv.height, data: imageData.data }, { pixelRatio: 2 });
    }

    if (!map.getLayer(layerId)) {
      try {
        map.addLayer({
          id: layerId, type: 'symbol',
          source: poiSourceName, 'source-layer': 'poi',
          filter: ['in', 'class', ...classes],
          minzoom: POI_MIN_ZOOM,
          layout: {
            'icon-image':         imgId,
            'icon-anchor':        'center',
            'icon-allow-overlap': true,
            'icon-size':          0.9,
            'text-field':         ['coalesce', ['get', 'name:en'], ['get', 'name'], ['get', 'name:latin'], ''],
            'text-font':          ['Noto Sans Regular'],
            'text-size':          ['interpolate', ['linear'], ['zoom'], 14, 10, 18, 12, 20, 13],
            'text-anchor':        'top',
            'text-offset':        [0, 1.2],
            'text-optional':      true,
            'text-allow-overlap': false,
            'text-max-width':     8,
            'visibility':         'none',
          },
          paint: {
            'text-color':      '#1f2937',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
        }, HALO_LAYER_ID);
      } catch (_) {}
    }
  });

  onDone?.();
}

// Toggles visibility of each category's custom pin layer.
function syncPoiFilter(map, poiState) {
  if (!map) return;
  POI_META.forEach(({ key }) => {
    const id = `poi-custom-${key}`;
    if (!map.getLayer(id)) return;
    try { map.setLayoutProperty(id, 'visibility', poiState[key] ? 'visible' : 'none'); } catch (_) {}
  });
}

// Registers click + cursor handlers on all POI pin layers.
// Clickable categories open Google Maps; non-clickable show a hover popup
// with the POI name so the label still carries useful information.
function registerPoiClickHandlers(map, poiClickRef, drawModeRef) {
  const hoverPopup = new maplibregl.Popup({
    closeButton: false, closeOnClick: false, offset: 14, className: 'fp-poi-hover',
  });
  const nameOf = (f) =>
    f?.properties?.['name:en'] || f?.properties?.name || f?.properties?.['name:latin'] || '';

  POI_META.forEach(({ key, clickable, label }) => {
    const layerId = `poi-custom-${key}`;
    if (!map.getLayer(layerId)) return;

    if (clickable) {
      map.on('click', layerId, (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const name = nameOf(feature);
        if (!name) return;
        poiClickRef.current = true;
        const { lng, lat } = e.lngLat;
        window.open(
          `https://www.google.com/maps/search/${encodeURIComponent(name)}/@${lat},${lng},18z`,
          '_blank', 'noopener'
        );
      });
      map.on('mouseenter', layerId, () => {
        if (!drawModeRef.current) map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layerId, () => {
        if (!drawModeRef.current) map.getCanvas().style.cursor = '';
      });
    } else {
      // Non-clickable: hover popup with the POI name + category label.
      map.on('mouseenter', layerId, (e) => {
        if (drawModeRef.current) return;
        map.getCanvas().style.cursor = 'help';
        const feature = e.features?.[0];
        const name = nameOf(feature) || label;
        hoverPopup
          .setLngLat(e.lngLat)
          .setHTML(`<div style="font-size:12px"><strong>${name}</strong><br/><span style="opacity:0.7">${label}</span></div>`)
          .addTo(map);
      });
      map.on('mousemove', layerId, (e) => {
        if (drawModeRef.current) return;
        hoverPopup.setLngLat(e.lngLat);
      });
      map.on('mouseleave', layerId, () => {
        if (!drawModeRef.current) map.getCanvas().style.cursor = '';
        hoverPopup.remove();
      });
    }
  });
}

// ── Nearest-POI layers ────────────────────────────────────────────────────────
// Two line layers on a single source: a thick white casing beneath a colored
// dashed line on top. Distance labels are rendered as HTML markers (see
// renderNearestPoiLabels) because CSS pills beat symbol-layer text for
// legibility on busy maps.
const NEAREST_CASING_ID = 'fp-nearest-poi-casing';
function ensureNearestPoiLayers(map) {
  if (map.getSource(NEAREST_SOURCE_ID)) return;
  map.addSource(NEAREST_SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON });

  if (!map.getLayer(NEAREST_CASING_ID)) {
    map.addLayer({
      id: NEAREST_CASING_ID, type: 'line', source: NEAREST_SOURCE_ID,
      paint: {
        'line-color':   '#ffffff',
        'line-width':   6,
        'line-opacity': 0.85,
        'line-blur':    0.4,
      },
    });
  }
  if (!map.getLayer(NEAREST_LINE_ID)) {
    map.addLayer({
      id: NEAREST_LINE_ID, type: 'line', source: NEAREST_SOURCE_ID,
      paint: {
        'line-color':     ['get', 'color'],
        'line-width':     3,
        'line-dasharray': [2, 1.5],
        'line-opacity':   1,
      },
    });
  }
}

// Scan currently loaded POI vector-tile features and produce a LineString
// feature for each active category connecting `from` to the nearest POI within
// NEAREST_MAX_KM. Returns a plain array (GeoJSON Features).
function computeNearestPoiFeatures(map, from, activeKeys) {
  const poiSourceName = (map.getStyle()?.layers || [])
    .find(l => l['source-layer'] === 'poi')?.source;
  if (!poiSourceName) return [];

  const all = map.querySourceFeatures(poiSourceName, { sourceLayer: 'poi' }) || [];
  const out = [];

  for (const key of activeKeys) {
    const meta = POI_META.find(m => m.key === key);
    if (!meta) continue;
    const classes = new Set(meta.classes);

    let best = null;
    for (const f of all) {
      if (!classes.has(f.properties?.class)) continue;
      const coords = f.geometry?.coordinates;
      if (!coords || coords.length < 2) continue;
      const d = turfDistance(from, coords, { units: 'kilometers' });
      if (d > NEAREST_MAX_KM) continue;
      if (!best || d < best.d) best = { d, coords };
    }
    if (!best) continue;

    const miles = best.d * 0.621371;
    const label = miles < 0.2
      ? `${Math.round(best.d * 3280.84)} ft`
      : `${miles.toFixed(1)} mi`;

    out.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [from, best.coords] },
      properties: { color: meta.color, label, key },
    });
  }
  return out;
}

// ── Draw layer helpers ────────────────────────────────────────────────────────
function ensureDrawLayers(map) {
  if (map.getSource(DRAW_SOURCE_ID)) return;
  map.addSource(DRAW_SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON });
  if (!map.getLayer(DRAW_FILL_ID)) {
    map.addLayer({
      id: DRAW_FILL_ID, type: 'fill', source: DRAW_SOURCE_ID,
      paint: { 'fill-color': THEME_BLUE, 'fill-opacity': 0.15 },
    }, LAYER_ID);
  }
  if (!map.getLayer(DRAW_LINE_ID)) {
    map.addLayer({
      id: DRAW_LINE_ID, type: 'line', source: DRAW_SOURCE_ID,
      paint: { 'line-color': THEME_BLUE, 'line-width': 2 },
    });
  }
  map.addSource(DRAW_SOURCE_VERTS, { type: 'geojson', data: EMPTY_GEOJSON });
  if (!map.getLayer(DRAW_VERT_ID)) {
    map.addLayer({
      id: DRAW_VERT_ID, type: 'circle', source: DRAW_SOURCE_VERTS,
      paint: {
        'circle-radius': 5, 'circle-color': THEME_BLUE,
        'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff',
      },
    });
  }
}

// Adds preview line (rubber-band) and radius label layers — called once after map loads
function ensurePreviewLayers(map) {
  if (!map.getSource(PREVIEW_SOURCE)) {
    map.addSource(PREVIEW_SOURCE, { type: 'geojson', data: EMPTY_GEOJSON });
  }
  if (!map.getLayer(PREVIEW_LAYER)) {
    map.addLayer({
      id: PREVIEW_LAYER, type: 'line', source: PREVIEW_SOURCE,
      paint: {
        'line-color': THEME_BLUE,
        'line-width': 1.5,
        'line-opacity': 0.75,
        'line-dasharray': [4, 3],
      },
    });
  }
  // Permanent finalized-shapes source (always visible, below property dots)
  if (!map.getSource(SHAPES_SOURCE_ID)) {
    map.addSource(SHAPES_SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON });
  }
  if (!map.getLayer(SHAPES_FILL_LAYER)) {
    map.addLayer({
      id: SHAPES_FILL_LAYER, type: 'fill', source: SHAPES_SOURCE_ID,
      paint: { 'fill-color': THEME_BLUE, 'fill-opacity': 0.15 },
    }, LAYER_ID);
  }
  if (!map.getLayer(SHAPES_LINE_LAYER)) {
    map.addLayer({
      id: SHAPES_LINE_LAYER, type: 'line', source: SHAPES_SOURCE_ID,
      paint: { 'line-color': THEME_BLUE, 'line-width': 2, 'line-opacity': 0.7 },
    });
  }

  if (!map.getSource(RADIUS_LABEL_SOURCE)) {
    map.addSource(RADIUS_LABEL_SOURCE, { type: 'geojson', data: EMPTY_GEOJSON });
  }
  if (!map.getLayer(RADIUS_LABEL_LAYER)) {
    map.addLayer({
      id: RADIUS_LABEL_LAYER, type: 'symbol', source: RADIUS_LABEL_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': THEME_BLUE,
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    });
  }
}

function clearDrawSources(map) {
  map.getSource(DRAW_SOURCE_ID)?.setData(EMPTY_GEOJSON);
  map.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
  map.getSource(PREVIEW_SOURCE)?.setData(EMPTY_GEOJSON);
  map.getSource(RADIUS_LABEL_SOURCE)?.setData(EMPTY_GEOJSON);
}

// ── Sanitize + client-side filter ────────────────────────────────────────────
function sanitizeFeatures(features = []) {
  return features.filter((f) => {
    const [lng, lat] = f.geometry?.coordinates || [];
    return typeof lng === 'number' && typeof lat === 'number'
      && lng >= -180 && lng <= 180
      && lat >= -90  && lat <= 90;
  });
}

function getDisplayGeojson(allGeojson, neighborhoodIds, zipCodes, drawnShapes) {
  const valid = sanitizeFeatures(allGeojson?.features);

  const hasShapes = drawnShapes && drawnShapes.length > 0;
  const nbhdSet   = neighborhoodIds?.length > 0 ? new Set(neighborhoodIds.map(Number)) : null;
  const zipSet    = zipCodes?.length  > 0 ? new Set(zipCodes) : null;
  const hasSpatialFilter = hasShapes || nbhdSet || zipSet;

  // No spatial filter active — show all city properties
  if (!hasSpatialFilter) return { type: 'FeatureCollection', features: valid };

  // Union: a property is shown if it falls inside ANY shape OR in ANY selected
  // neighborhood OR in any selected zip. No dedup needed — filtering from unique source.
  const display = valid.filter(f => {
    if (hasShapes && drawnShapes.some(shape => {
      try { return booleanPointInPolygon(f, shape); } catch (_) { return false; }
    })) return true;
    if (nbhdSet && nbhdSet.has(Number(f.properties?.neighborhood_id))) return true;
    if (zipSet  && zipSet.has(f.properties?.zip)) return true;
    return false;
  });

  return { type: 'FeatureCollection', features: display };
}

// ── Property layer helpers ────────────────────────────────────────────────────
// Both sets share the same paint — only the source and layer IDs differ.
// `beforeId` controls z-order insertion; pass undefined to append to top.
function addClusteredLayers(map, beforeId) {
  map.addLayer({
    id: HALO_LAYER_ID, type: 'circle', source: SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 14],
      'circle-color': '#FF5C35', 'circle-opacity': 0.22,
      'circle-blur': 0.6, 'circle-stroke-width': 0 },
  }, beforeId);
  map.addLayer({
    id: CLUSTER_LAYER_ID, type: 'circle', source: SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: { 'circle-color': '#FF5C35', 'circle-opacity': 0.85,
      'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff',
      'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 28] },
  }, beforeId);
  map.addLayer({
    id: LAYER_ID, type: 'circle', source: SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 14, 7],
      'circle-color': '#FF5C35', 'circle-stroke-width': 1.5,
      'circle-stroke-color': '#ffffff', 'circle-opacity': 0.90 },
  }, beforeId);
  map.addLayer({
    id: CLUSTER_COUNT_ID, type: 'symbol', source: SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: { 'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'], 'text-size': 12 },
    paint: { 'text-color': '#ffffff' },
  }, beforeId);
}

function addIndividualLayers(map, beforeId) {
  map.addLayer({
    id: IND_HALO_LAYER_ID, type: 'circle', source: SOURCE_INDIVIDUAL_ID,
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 14],
      'circle-color': '#FF5C35', 'circle-opacity': 0.22,
      'circle-blur': 0.6, 'circle-stroke-width': 0 },
  }, beforeId);
  map.addLayer({
    id: IND_LAYER_ID, type: 'circle', source: SOURCE_INDIVIDUAL_ID,
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 14, 7],
      'circle-color': '#FF5C35', 'circle-stroke-width': 1.5,
      'circle-stroke-color': '#ffffff', 'circle-opacity': 0.90 },
  }, beforeId);
}

function addHeartImageAndShortlistedLayers(map) {
  const size = 28;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const s = size;
  ctx.fillStyle = '#e0245e';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(s * 0.5, s * 0.85);
  ctx.bezierCurveTo(s * 0.1, s * 0.6,  s * 0.1,  s * 0.3,  s * 0.25, s * 0.2);
  ctx.bezierCurveTo(s * 0.35, s * 0.1, s * 0.5,  s * 0.15, s * 0.5,  s * 0.3);
  ctx.bezierCurveTo(s * 0.5,  s * 0.15, s * 0.65, s * 0.1, s * 0.75, s * 0.2);
  ctx.bezierCurveTo(s * 0.9, s * 0.3,  s * 0.9,  s * 0.6,  s * 0.5,  s * 0.85);
  ctx.fill();
  ctx.stroke();
  map.addImage('fp-heart', ctx.getImageData(0, 0, size, size));

  const emptyFilter = ['boolean', false];
  const iconLayout = {
    'icon-image': 'fp-heart',
    'icon-size': 1,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  };

  map.addLayer({
    id: SHORTLISTED_CLUSTER_LAYER,
    type: 'symbol',
    source: SOURCE_ID,
    filter: emptyFilter,
    layout: iconLayout,
  });
  map.addLayer({
    id: SHORTLISTED_IND_LAYER,
    type: 'symbol',
    source: SOURCE_INDIVIDUAL_ID,
    filter: emptyFilter,
    layout: iconLayout,
  });
  // Start hidden — cluster toggle effect controls visibility based on mode
  map.setLayoutProperty(SHORTLISTED_IND_LAYER, 'visibility', 'none');
}

// ── syncMapData ───────────────────────────────────────────────────────────────
function syncMapData(map, geojson, fitBoundsOpts = null) {
  if (!map) return;
  const source = map.getSource(SOURCE_ID);
  if (source) source.setData(geojson);
  // Keep individual source in sync so toggle is instant
  map.getSource(SOURCE_INDIVIDUAL_ID)?.setData(geojson);

  if (fitBoundsOpts === false) return;

  const features = geojson?.features || [];
  if (features.length > 0) {
    const coords = features.map((f) => f.geometry.coordinates);
    const lngs   = coords.map((c) => c[0]);
    const lats   = coords.map((c) => c[1]);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      fitBoundsOpts ?? { padding: 60, maxZoom: 13, duration: 600 },
    );
  }
}

// ── Map initialiser ───────────────────────────────────────────────────────────
function initMap(container, interactive, onReady) {
  const map = new maplibregl.Map({
    container,
    style: MAP_STYLE,
    center: INITIAL_CENTER,
    zoom: INITIAL_ZOOM,
    interactive,
    attributionControl: false,
  });

  if (interactive) {
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new maplibregl.AttributionControl({ compact: false }), 'bottom-left');
  }

  map.on('load', () => {
    // Clustered source — MapLibre groups nearby points at zoom ≤ 11
    map.addSource(SOURCE_ID, {
      type: 'geojson', data: EMPTY_GEOJSON,
      cluster: true, clusterMaxZoom: 11, clusterRadius: 40,
    });
    // Individual source — same data, no clustering (used when user prefers individual view)
    map.addSource(SOURCE_INDIVIDUAL_ID, { type: 'geojson', data: EMPTY_GEOJSON });

    addClusteredLayers(map, undefined);   // visible by default (cluster is default pref)
    addIndividualLayers(map, undefined);  // added now, hidden — toggled via preference

    // Individual layers start hidden; the cluster toggle effect in ExpandedMapOverlay
    // will apply the correct visibility based on the saved localStorage preference.
    [IND_HALO_LAYER_ID, IND_LAYER_ID].forEach(id =>
      map.setLayoutProperty(id, 'visibility', 'none'));

    onReady(map);
  });

  // POI sprite images: some features have icon-image expressions that evaluate to ""
  // when no icon is defined for their subclass. Add a transparent 1×1 placeholder to
  // prevent MapLibre from blocking layer rendering on missing image names.
  map.on('styleimagemissing', ({ id }) => {
    try {
      if (!map.hasImage(id)) {
        map.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) });
      }
    } catch (_) {}
  });

  return map;
}

// ── MiniMap ───────────────────────────────────────────────────────────────────
function MiniMap({ geojson, drawnShape, loading, onExpand, neighborhoodsGeojson, selectedNeighborhoodIds }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [ready,   setReady]   = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = initMap(containerRef.current, false, (m) => {
      m.addSource(DRAW_SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON });
      m.addLayer({
        id: DRAW_FILL_ID, type: 'fill', source: DRAW_SOURCE_ID,
        paint: { 'fill-color': THEME_BLUE, 'fill-opacity': 0.12 },
      }, LAYER_ID);
      m.addLayer({
        id: DRAW_LINE_ID, type: 'line', source: DRAW_SOURCE_ID,
        paint: { 'line-color': THEME_BLUE, 'line-width': 1.5, 'line-opacity': 0.7 },
      });
      mapRef.current = m;
      setReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setReady(false); };
  }, []);

  useEffect(() => {
    if (ready && mapRef.current) syncMapData(mapRef.current, geojson);
  }, [geojson, ready]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const shapes = Array.isArray(drawnShape) ? drawnShape : (drawnShape ? [drawnShape] : []);
    if (shapes.length > 0) {
      mapRef.current.getSource(DRAW_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: shapes });
    } else {
      mapRef.current.getSource(DRAW_SOURCE_ID)?.setData(EMPTY_GEOJSON);
    }
  }, [drawnShape, ready]);

  // Neighborhood overlay visibility — mirrors the `fp_nbhd_visible` preference
  // used by the expanded panel. Defaults to hidden when the key is absent.
  // Reacts to cross-tab ('storage') and same-tab (custom event dispatched by
  // the expanded panel's persist effect) changes.
  const [nbhdVisible, setNbhdVisible] = useState(() => {
    try { return localStorage.getItem('fp_nbhd_visible') === 'true'; } catch (_) { return false; }
  });
  useEffect(() => {
    const read = () => {
      try { setNbhdVisible(localStorage.getItem('fp_nbhd_visible') === 'true'); } catch (_) {}
    };
    const onStorage = (e) => { if (e.key === 'fp_nbhd_visible') read(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('fp-nbhd-visible-changed', read);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('fp-nbhd-visible-changed', read);
    };
  }, []);

  // Neighborhood overlay — gated by nbhdVisible.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    if (nbhdVisible && neighborhoodsGeojson) {
      const data = typeof neighborhoodsGeojson === 'string'
        ? JSON.parse(neighborhoodsGeojson) : neighborhoodsGeojson;
      const src = map.getSource(OVERLAY_CONFIGS.neighborhoods.sourceId);
      if (src) src.setData(data);
      else     addOverlay(map, 'neighborhoods', data);
    } else {
      removeOverlay(map, 'neighborhoods');
    }
  }, [neighborhoodsGeojson, ready, nbhdVisible]);

  // Highlight selected neighborhoods
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    if (!map.getLayer('fp-nbhd-fill')) return;
    const selectedIds = (selectedNeighborhoodIds || []).map(Number);
    map.setPaintProperty('fp-nbhd-fill', 'fill-opacity',
      selectedIds.length > 0
        ? ['case',
            ['in',
              ['to-number', ['coalesce', ['get', 'neighborhood_id'], ['id'], ['get', 'id'], 0]],
              ['literal', selectedIds],
            ],
            0.35, 0.08]
        : 0.08
    );
  }, [selectedNeighborhoodIds, ready, neighborhoodsGeojson]);

  return (
    <div
      className='location-map-mini-wrapper'
      onClick={onExpand}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div ref={containerRef} className='location-map-mini' />
      {loading && (
        <div className='location-map-loading'>
          <Icon icon='spinner' spin style={{ fontSize: 20, color: '#999' }} />
        </div>
      )}
      <div className={`location-map-mini-overlay${hovered ? ' visible' : ''}`}>
        <span>Explore map filters &rarr;</span>
      </div>
    </div>
  );
}

// ── PoiSubmenu ────────────────────────────────────────────────────────────────
function PoiSubmenu({ poi, onTogglePoi, onSetAllPois, hasCity }) {
  const anyOn  = POI_META.some(m => poi[m.key]);
  const disabled = !hasCity;
  return (
    <div className='location-poi-submenu'>
      <div
        className='location-poi-submenu__bulk'
        style={{ padding: '4px 6px 6px' }}
      >
        <button
          type='button'
          className='location-poi-submenu__bulk-btn'
          disabled={disabled}
          title={disabled ? 'Select a city first' : undefined}
          onClick={(e) => { e.stopPropagation(); onSetAllPois?.(!anyOn); }}
          style={{
            width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 4,
            border: '1px solid #d1d5db',
            background: disabled ? '#f3f4f6' : '#fff',
            color:   disabled ? '#9ca3af' : '#111827',
            cursor:  disabled ? 'not-allowed' : 'pointer',
            fontWeight: 500,
          }}
        >{anyOn ? 'Clear all' : 'Show all'}</button>
      </div>
      {POI_META.map(({ key, icon, label, color }) => (
        <button
          key={key}
          className={`location-poi-submenu__row${poi[key] && !disabled ? ' is-active' : ''}`}
          style={
            disabled
              ? { borderLeft: `4px solid ${color}`, opacity: 0.55, cursor: 'not-allowed' }
              : (poi[key]
                  ? { background: color, color: '#fff', boxShadow: `0 2px 6px ${color}55`, borderLeft: `4px solid ${color}` }
                  : { borderLeft: `4px solid ${color}` })
          }
          title={disabled ? 'Select a city first' : undefined}
          onClick={(e) => { e.stopPropagation(); onTogglePoi(key); }}
          aria-pressed={poi[key]}
        >
          <Icon icon={icon} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

// ── PoiBottomSheet — touch variant of PoiSubmenu (4-col grid + backdrop) ─────
function PoiBottomSheet({ poi, onTogglePoi, onSetAllPois, hasCity, onClose }) {
  const anyOn = POI_META.some(m => poi[m.key]);
  const stop = (e) => e.stopPropagation();
  return ReactDOM.createPortal(
    <>
      <div className='poi-bottom-sheet-backdrop' onClick={onClose} />
      <div className='poi-bottom-sheet' onMouseDown={stop} onClick={stop}>
        <div className='poi-bottom-sheet__handle' />
        <div className='poi-bottom-sheet__header'>
          <span className='poi-bottom-sheet__title'>Points of Interest</span>
          <button
            type='button'
            className='poi-bottom-sheet__close'
            onClick={onClose}
            aria-label='Close'
          >
            <Icon icon='times' />
          </button>
        </div>
        <button
          type='button'
          className='poi-bottom-sheet__bulk'
          disabled={!hasCity}
          title={!hasCity ? 'Select a city first' : undefined}
          onClick={() => onSetAllPois?.(!anyOn)}
        >
          {anyOn ? 'Clear all' : 'Show all'}
        </button>
        <div className='poi-bottom-sheet__grid'>
          {POI_META.map(({ key, icon, label, color }) => {
            const active = poi[key] && hasCity;
            return (
              <button
                key={key}
                type='button'
                className={`poi-bottom-sheet__tile${active ? ' is-active' : ''}`}
                disabled={!hasCity}
                onClick={() => onTogglePoi(key)}
                style={active
                  ? { background: color, color: '#fff', borderColor: color }
                  : { borderColor: color }}
                aria-pressed={!!poi[key]}
              >
                <Icon icon={icon} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  );
}

// ── MapToolbar ────────────────────────────────────────────────────────────────
function MapToolbar({
  drawMode, onDrawMode,
  shapesCount,
  editMode, onToggleEdit,
  moveMode, onToggleMove,
  markerMode, onCycleMarkerMode,
  autoZoom, onToggleAutoZoom,
  nbhdVisible, onToggleNbhd,
  poi, onTogglePoi, onSetAllPois, hasCity,
  onClearAll,
  onUndo,
  isTouch,
}) {
  const hasShape = shapesCount > 0;
  const [poiMenuOpen, setPoiMenuOpen] = React.useState(false);
  const poiWrapRef = React.useRef(null);
  const anyPoiActive = Object.values(poi).some(Boolean);
  // On touch breakpoints, polygon is the only viable draw tool — drag-based
  // tools (freehand, radius, rectangle) are imprecise with a finger.
  const drawModesToShow = isTouch
    ? DRAW_MODES.filter(({ key }) => key === 'polygon')
    : DRAW_MODES;

  // Close POI submenu when draw mode activates
  React.useEffect(() => {
    if (drawMode) setPoiMenuOpen(false);
  }, [drawMode]);

  // Close POI submenu on outside click (desktop only — bottom sheet has its own backdrop)
  React.useEffect(() => {
    if (!poiMenuOpen || isTouch) return;
    const handler = (e) => {
      if (!poiWrapRef.current?.contains(e.target)) setPoiMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [poiMenuOpen, isTouch]);

  return (
    <div className='location-toolbar'>

      {/* Combined group: draw tools + thin spacer + edit/move/undo/clear */}
      <div className='location-toolbar__group'>
        {drawModesToShow.map(({ key, icon, label }) => (
          <button
            key={key}
            className={`location-toolbar__btn${drawMode === key ? ' is-active' : ''}`}
            data-tooltip={label}
            onClick={() => onDrawMode(key)}
            aria-label={label}
            aria-pressed={drawMode === key}
          >
            <Icon icon={icon} />
          </button>
        ))}

        <span className='location-toolbar__inner-sep' />

        {/* Edit and Move are pointer-only — drag interactions don't work well on touch */}
        {!isTouch && (
          <>
            <button
              className={`location-toolbar__btn${editMode ? ' is-active' : ''}${!hasShape ? ' is-disabled' : ''}`}
              data-tooltip='Edit shape'
              onClick={hasShape ? onToggleEdit : undefined}
              aria-label='Edit shape'
              aria-pressed={editMode}
              disabled={!hasShape}
            >
              <Icon icon='pen' />
            </button>
            <button
              className={`location-toolbar__btn${moveMode ? ' is-active' : ''}${!hasShape ? ' is-disabled' : ''}`}
              data-tooltip='Move shape'
              onClick={hasShape ? onToggleMove : undefined}
              aria-label='Move shape'
              aria-pressed={moveMode}
              disabled={!hasShape}
            >
              <Icon icon='arrows-alt' />
            </button>
          </>
        )}
        <button
          className={`location-toolbar__btn${!hasShape ? ' is-disabled' : ''}`}
          data-tooltip='Undo last shape'
          onClick={hasShape ? onUndo : undefined}
          aria-label='Undo last drawn shape'
          disabled={!hasShape}
        >
          <Icon icon='undo' />
        </button>
        <button
          className={`location-toolbar__btn location-toolbar__btn--danger${!hasShape && !drawMode ? ' is-disabled' : ''}`}
          data-tooltip={shapesCount > 1 ? `Clear ${shapesCount} shapes` : 'Clear shape'}
          onClick={onClearAll}
          aria-label='Clear drawn shape'
          disabled={!hasShape && !drawMode}
        >
          <Icon icon='eraser' />
        </button>
      </div>

      <div className='location-toolbar__separator' />

      {/* Display group: markers (tri-state) + nbhd + zoom lock + sep + POI */}
      <div className='location-toolbar__group'>
        {(() => {
          const markerMeta = {
            cluster:    { icon: 'dot-circle',    label: 'Clustered markers',   next: 'Individual' },
            individual: { icon: 'map-marker-alt', label: 'Individual markers',  next: 'Hidden' },
            hidden:     { icon: 'eye-slash',      label: 'Markers hidden',      next: 'Clustered' },
          };
          const meta = markerMeta[markerMode] || markerMeta.cluster;
          return (
            <button
              className={`location-toolbar__btn${markerMode !== 'hidden' ? ' is-active' : ''}`}
              data-tooltip={`${meta.label} — click for ${meta.next}`}
              onClick={onCycleMarkerMode}
              aria-label='Cycle property marker mode'
            >
              <Icon icon={meta.icon} />
            </button>
          );
        })()}

        <button
          className={`location-toolbar__btn${nbhdVisible ? ' is-active' : ''}`}
          data-tooltip={nbhdVisible ? 'Hide neighborhood boundaries' : 'Show neighborhood boundaries'}
          onClick={onToggleNbhd}
          aria-label='Toggle neighborhood boundaries'
          aria-pressed={nbhdVisible}
        >
          <Icon icon='draw-polygon' />
        </button>

        <button
          className={`location-toolbar__btn${!autoZoom ? ' is-active' : ''}`}
          data-tooltip={autoZoom ? 'Auto-zoom enabled — click to disable' : 'Auto-zoom disabled — click to enable'}
          onClick={onToggleAutoZoom}
          aria-label='Toggle auto-zoom'
          aria-pressed={!autoZoom}
        >
          <Icon icon={autoZoom ? 'search-plus' : 'search-minus'} />
        </button>

        <span className='location-toolbar__inner-sep' />

        <div ref={poiWrapRef} className='location-poi-wrap'>
          <button
            className={`location-toolbar__btn${anyPoiActive ? ' is-active' : ''}`}
            data-tooltip={poiMenuOpen ? undefined : 'Points of Interest'}
            onClick={(e) => { e.stopPropagation(); setPoiMenuOpen(v => !v); }}
            aria-label='Points of Interest'
            aria-pressed={anyPoiActive}
          >
            <Icon icon='map-pin' />
          </button>
          {poiMenuOpen && (
            isTouch
              ? <PoiBottomSheet
                  poi={poi} onTogglePoi={onTogglePoi} onSetAllPois={onSetAllPois}
                  hasCity={hasCity} onClose={() => setPoiMenuOpen(false)}
                />
              : <PoiSubmenu poi={poi} onTogglePoi={onTogglePoi} onSetAllPois={onSetAllPois} hasCity={hasCity} />
          )}
        </div>
      </div>

    </div>
  );
}

// ── ExpandedMapOverlay ────────────────────────────────────────────────────────
function ExpandedMapOverlay({
  allGeojson, loading,
  filters, setFilters,
  cityOptions, neighborhoodOptions, zipOptions,
  neighborhoodsGeojson,
  onClose,
  totalMatched = 0,
}) {
  const { isMobile, isTablet, isTouch } = useTouchBreakpoint();
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const [ready,     setReady]     = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Neighborhood boundary overlay visibility — persisted, default false
  const [nbhdVisible, setNbhdVisible] = useState(() => {
    try { return localStorage.getItem('fp_nbhd_visible') === 'true'; } catch (_) { return false; }
  });

  // POI click guard — prevents map click from firing after a POI click
  const poiClickRef = useRef(false);

  // Draw state
  const [drawMode,      setDrawMode]      = useState(null);
  const [drawnShapes,   setDrawnShapes]   = useState(filters.drawnShape || []);
  const [radiusCenter,  setRadiusCenter]  = useState(null);
  const [radiusMiles,   setRadiusMiles]   = useState(1);
  const drawnShapesRef = useRef([]);
  drawnShapesRef.current = drawnShapes; // always-current for event-handler closures
  const drawModeRef        = useRef(null);
  const polygonVerticesRef = useRef([]);
  const freehandPointsRef  = useRef([]);
  const isDrawingRef       = useRef(false);
  drawModeRef.current      = drawMode;
  // Tracks last seen cityId so we can detect a mid-session city change vs initial mount.
  const cityChangeRef      = useRef(filters.cityId);

  // Shape editor state
  const [editMode,          setEditMode]          = useState(false);
  const [moveMode,          setMoveMode]          = useState(false);
  const [selectedShapeIdx,  setSelectedShapeIdx]  = useState(null); // index into drawnShapes; null = no selection
  const editModeRef                       = useRef(false);
  const draggingVertexIdxRef              = useRef(-1);
  const editVerticesRef                   = useRef([]);
  const isDraggingRadiusRef               = useRef(false);
  editModeRef.current = editMode;

  // Property marker mode — tri-state (cluster | individual | hidden), persisted to localStorage
  const [markerMode, setMarkerMode] = useState(() => {
    try {
      const v = localStorage.getItem('fp_marker_mode');
      return ['cluster', 'individual', 'hidden'].includes(v) ? v : 'cluster';
    } catch (_) { return 'cluster'; }
  });
  const cycleMarkerMode = () => {
    setMarkerMode(m => (m === 'cluster' ? 'individual' : m === 'individual' ? 'hidden' : 'cluster'));
  };

  // POI toggles — 10 OSM categories; persisted so re-opening the map keeps selection
  const [poi, setPoi] = useState(() => {
    try {
      const raw = localStorage.getItem(POI_STORAGE_KEY);
      if (raw) return { ...POI_DEFAULT_STATE, ...JSON.parse(raw) };
    } catch (_) {}
    return { ...POI_DEFAULT_STATE };
  });

  useEffect(() => {
    try { localStorage.setItem(POI_STORAGE_KEY, JSON.stringify(poi)); } catch (_) {}
  }, [poi]);

  // Brief pill shown at bottom-center of the map (e.g. "Zoom in…", "Select a city…").
  // `at` is a timestamp so repeat triggers re-arm the 3s auto-dismiss timer.
  const [poiHint, setPoiHint] = useState({ visible: false, text: '', at: 0 });
  useEffect(() => {
    if (!poiHint.visible) return;
    const t = setTimeout(() => setPoiHint(h => ({ ...h, visible: false })), 3000);
    return () => clearTimeout(t);
  }, [poiHint.at]);

  // Auto-zoom toggle — persisted to localStorage
  const [autoZoom, setAutoZoom] = useState(() => {
    try { return localStorage.getItem('fp_auto_zoom') === 'true'; } catch (_) { return false; }
  });

  // History for marker popover navigation
  const history = useHistory();

  // Shortlist state — shared with card/list view via Recoil
  const [selectedProperties, setSelectedProperties] = useRecoilState(selectedPropertiesAtom);

  // Hovered individual marker for popover
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const markerClickedRef        = useRef(false);
  const hasInitialFitRef        = useRef(false);
  const popoverRef              = useRef(null);
  const hoveredMarkerLngLatRef  = useRef(null);

  // ── displayCount ─────────────────────────────────────────────────────────────
  const displayCount = useMemo(
    () => getDisplayGeojson(allGeojson, filters.neighborhoodIds, filters.zipCodes, drawnShapes)?.features?.length ?? 0,
    [allGeojson, filters.neighborhoodIds, filters.zipCodes, drawnShapes],
  );

  // ── Animation + close ────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setFilters(f => ({ ...f, drawnShape: drawnShapes }));
    setIsClosing(true);
  }, [drawnShapes, setFilters]);
  const handleAnimationEnd = useCallback(() => { if (isClosing) onClose(); }, [isClosing, onClose]);

  // Esc closes the map overlay only — never the parent (Reactstrap modal on
  // mobile/iPad also listens for Escape, via onKeyUp on its modal-content).
  // Capture-phase listeners on BOTH keydown and keyup, with stopPropagation,
  // prevent either event from reaching the modal's React onKeyUp handler.
  //
  // Two-stage on keydown: first Esc cancels an in-progress draw (preserves
  // already-committed shapes); second Esc closes the overlay.
  useEffect(() => {
    const swallow = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (e.type !== 'keydown') return;
      const inProgress = Boolean(drawModeRef.current) && (
        polygonVerticesRef.current.length > 0 ||
        freehandPointsRef.current.length > 0 ||
        radiusCenter !== null ||
        isDrawingRef.current
      );
      if (inProgress) {
        setRadiusCenter(null);
        polygonVerticesRef.current = [];
        freehandPointsRef.current  = [];
        isDrawingRef.current = false;
        if (mapRef.current) {
          mapRef.current.getSource(DRAW_SOURCE_ID)?.setData(EMPTY_GEOJSON);
          mapRef.current.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
          mapRef.current.getSource(PREVIEW_SOURCE)?.setData(EMPTY_GEOJSON);
          mapRef.current.getSource(RADIUS_LABEL_SOURCE)?.setData(EMPTY_GEOJSON);
        }
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', swallow, { capture: true });
    document.addEventListener('keyup', swallow, { capture: true });
    return () => {
      document.removeEventListener('keydown', swallow, { capture: true });
      document.removeEventListener('keyup', swallow, { capture: true });
    };
  }, [handleClose, radiusCenter]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ── Map init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const map = initMap(containerRef.current, true, (m) => {
      mapRef.current = m;
      ensurePreviewLayers(m);
      addHeartImageAndShortlistedLayers(m);
      addCustomPoiLayers(m, () => {
        registerPoiClickHandlers(m, poiClickRef, drawModeRef);
        ensureNearestPoiLayers(m);
        syncPoiFilter(m, poi); // apply persisted selection
      });
      setReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setReady(false); };
  }, []);

  // ── Sync property dots + fit to visible markers ──────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    if (editMode || moveMode || isDraggingRadiusRef.current) return;
    const display = getDisplayGeojson(allGeojson, filters.neighborhoodIds, filters.zipCodes, drawnShapes);
    const onlyShapes = drawnShapes.length > 0 && !filters.neighborhoodIds?.length && !filters.zipCodes?.length;
    const onlyNeighborhoods = filters.neighborhoodIds?.length > 0 && drawnShapes.length === 0;
    syncMapData(mapRef.current, display,
      (onlyShapes || onlyNeighborhoods)
        ? false
        : { padding: { top: 120, bottom: 60, left: 60, right: 80 }, maxZoom: 13, duration: 600 }
    );
  }, [allGeojson, ready, filters.neighborhoodIds, filters.zipCodes, drawnShapes, editMode, moveMode]);

  // ── Clear DRAW_SOURCE_ID when not actively drawing / editing / moving ─────────
  useEffect(() => {
    if (!ready || !mapRef.current || drawMode || editMode || moveMode) return;
    mapRef.current.getSource(DRAW_SOURCE_ID)?.setData(EMPTY_GEOJSON);
    mapRef.current.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
  }, [drawMode, editMode, moveMode, ready]);

  // ── Sync all finalized shapes → SHAPES_SOURCE_ID (tagged with _idx for opacity expressions) ──
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const features = drawnShapes.map((s, i) => ({
      ...s, properties: { ...(s.properties || {}), _idx: i },
    }));
    mapRef.current.getSource(SHAPES_SOURCE_ID)?.setData({ type: 'FeatureCollection', features });
  }, [drawnShapes, ready]);

  // ── Selection opacity: hide selected shape (shown on DRAW_SOURCE_ID); dim others ──
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    if (selectedShapeIdx !== null) {
      map.setPaintProperty(SHAPES_FILL_LAYER, 'fill-opacity',
        ['case', ['==', ['get', '_idx'], selectedShapeIdx], 0.0, 0.07]);
      map.setPaintProperty(SHAPES_LINE_LAYER, 'line-opacity',
        ['case', ['==', ['get', '_idx'], selectedShapeIdx], 0.0, 0.35]);
    } else {
      map.setPaintProperty(SHAPES_FILL_LAYER, 'fill-opacity', 0.15);
      map.setPaintProperty(SHAPES_LINE_LAYER, 'line-opacity', 0.70);
    }
  }, [selectedShapeIdx, ready]);

  // ── Marker mode: cluster / individual / hidden ───────────────────────────────
  // Persists preference to localStorage; toggles layer visibility without rebuilding sources.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    try { localStorage.setItem('fp_marker_mode', markerMode); } catch (_) {}
    const map = mapRef.current;
    const clusterLayers    = [HALO_LAYER_ID, CLUSTER_LAYER_ID, LAYER_ID, CLUSTER_COUNT_ID, SHORTLISTED_CLUSTER_LAYER];
    const individualLayers = [IND_HALO_LAYER_ID, IND_LAYER_ID, SHORTLISTED_IND_LAYER];
    const showCluster    = markerMode === 'cluster';
    const showIndividual = markerMode === 'individual';
    clusterLayers.forEach(id => {
      try { map.setLayoutProperty(id, 'visibility', showCluster ? 'visible' : 'none'); } catch (_) {}
    });
    individualLayers.forEach(id => {
      try { map.setLayoutProperty(id, 'visibility', showIndividual ? 'visible' : 'none'); } catch (_) {}
    });
  }, [markerMode, ready]);

  // ── Persist auto-zoom + neighborhood visibility preferences ──────────────────
  useEffect(() => {
    try { localStorage.setItem('fp_auto_zoom', autoZoom ? 'true' : 'false'); } catch (_) {}
  }, [autoZoom]);

  useEffect(() => {
    try { localStorage.setItem('fp_nbhd_visible', nbhdVisible ? 'true' : 'false'); } catch (_) {}
    // Notify same-tab listeners (MiniMap) — the native 'storage' event only
    // fires in *other* tabs, so we dispatch a custom event for this tab.
    try { window.dispatchEvent(new CustomEvent('fp-nbhd-visible-changed', { detail: nbhdVisible })); } catch (_) {}
  }, [nbhdVisible]);

  // ── Shortlisted heart markers — update filter when selectedProperties changes ──
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const ids = selectedProperties.map(p => Number(p.propertyId));
    const clusterFilter = ids.length > 0
      ? ['all', ['!', ['has', 'point_count']], ['in', ['to-number', ['get', 'id']], ['literal', ids]]]
      : ['boolean', false];
    const indFilter = ids.length > 0
      ? ['in', ['to-number', ['get', 'id']], ['literal', ids]]
      : ['boolean', false];
    try {
      mapRef.current.setFilter(SHORTLISTED_CLUSTER_LAYER, clusterFilter);
      mapRef.current.setFilter(SHORTLISTED_IND_LAYER, indFilter);
    } catch (_) {}
  }, [selectedProperties, ready]);

  // ── POI visibility mirrors city selection ────────────────────────────────────
  // With no city chosen we hide every POI layer regardless of the user's toggle
  // state (their selection persists and restores when a city is selected again).
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const m = mapRef.current;
    if (!filters.cityId) {
      syncPoiFilter(m, POI_DEFAULT_STATE);
    } else {
      syncPoiFilter(m, poi);
    }
  }, [filters.cityId, ready]);

  // ── Neighborhoods overlay ────────────────────────────────────────────────────
  // Controlled entirely by nbhdVisible toggle — no auto-enable on city select.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    if (nbhdVisible && neighborhoodsGeojson) {
      const raw = typeof neighborhoodsGeojson === 'string'
        ? JSON.parse(neighborhoodsGeojson) : neighborhoodsGeojson;
      const data = {
        ...raw,
        features: raw.features.map(f => ({
          ...f,
          properties: { ...f.properties, area: turfArea(f) },
        })),
      };
      const src = mapRef.current.getSource(OVERLAY_CONFIGS.neighborhoods.sourceId);
      if (src) {
        src.setData(data);
      } else {
        addOverlay(mapRef.current, 'neighborhoods', data);
      }
    } else if (!nbhdVisible) {
      removeOverlay(mapRef.current, 'neighborhoods');
    }
  }, [nbhdVisible, neighborhoodsGeojson, ready]);

  // ── Clear drawn shape when city changes ───────────────────────────────────────
  // The atom's drawnShape is reset to null by the city dropdown onChange, but local
  // component state (drawnShape, drawMode, etc.) is only initialised from the atom
  // at mount — it never syncs back automatically. This effect detects a mid-session
  // city change and runs the same cleanup as handleClearAll (minus the setFilters
  // call, which the dropdown already handled).
  useEffect(() => {
    if (cityChangeRef.current === filters.cityId) return; // same city — skip initial mount
    cityChangeRef.current = filters.cityId;
    setDrawnShapes([]);
    setSelectedShapeIdx(null);
    setDrawMode(null);
    setEditMode(false);
    setMoveMode(false);
    setRadiusCenter(null);
    polygonVerticesRef.current = [];
    freehandPointsRef.current  = [];
    isDrawingRef.current = false;
    editVerticesRef.current = [];
    if (mapRef.current) {
      mapRef.current.dragPan?.enable();
      mapRef.current.getCanvas().style.cursor = '';
      clearDrawSources(mapRef.current);
    }
  }, [filters.cityId]);

  // ── Highlight selected neighborhoods ─────────────────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    if (!map.getLayer('fp-nbhd-fill')) return;
    const selectedIds = (filters.neighborhoodIds || []).map(Number);
    map.setPaintProperty('fp-nbhd-fill', 'fill-opacity',
      selectedIds.length > 0
        ? ['case',
            ['in',
              ['to-number', ['coalesce', ['get', 'neighborhood_id'], ['id'], ['get', 'id'], 0]],
              ['literal', selectedIds],
            ],
            0.35, 0.08]
        : 0.08
    );
  }, [filters.neighborhoodIds, ready, nbhdVisible, neighborhoodsGeojson]);

  // ── Neighborhood polygon click → select/deselect ──────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || !nbhdVisible) return;
    const map = mapRef.current;
    if (!map.getLayer('fp-nbhd-fill')) return;

    const onNbhdClick = (e) => {
      if (drawModeRef.current || editModeRef.current) return;
      if (map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER_ID] }).length > 0) return;
      if (map.queryRenderedFeatures(e.point, { layers: [LAYER_ID, IND_LAYER_ID] }).length > 0) return;
      const feature = e.features?.[0];
      if (!feature) return;
      const nbhdId = Number(feature.properties?.neighborhood_id ?? feature.id ?? feature.properties?.id);
      if (!nbhdId) return;
      setFilters((f) => {
        const ids    = (f.neighborhoodIds || []).map(Number);
        const newIds = ids.includes(nbhdId) ? ids.filter(id => id !== nbhdId) : [...ids, nbhdId];
        return { ...f, neighborhoodIds: newIds, offset: 0 };
      });
    };
    const onNbhdMove = (e) => {
      if (drawModeRef.current || editModeRef.current) return;
      map.getCanvas().style.cursor = 'pointer';
      const nbhdId = Number(e.features?.[0]?.properties?.neighborhood_id ?? e.features?.[0]?.id ?? e.features?.[0]?.properties?.id);
      if (nbhdId && map.getLayer('fp-nbhd-hover')) {
        map.setFilter('fp-nbhd-hover', ['==', ['get', 'neighborhood_id'], nbhdId]);
      }
    };
    const onNbhdLeave = () => {
      if (drawModeRef.current || editModeRef.current) return;
      map.getCanvas().style.cursor = '';
      if (map.getLayer('fp-nbhd-hover')) {
        map.setFilter('fp-nbhd-hover', ['==', ['get', 'neighborhood_id'], -1]);
      }
    };

    map.on('click',      'fp-nbhd-fill', onNbhdClick);
    map.on('mousemove',  'fp-nbhd-fill', onNbhdMove);
    map.on('mouseleave', 'fp-nbhd-fill', onNbhdLeave);

    return () => {
      try {
        if (map.getLayer('fp-nbhd-fill')) {
          map.off('click',      'fp-nbhd-fill', onNbhdClick);
          map.off('mousemove',  'fp-nbhd-fill', onNbhdMove);
          map.off('mouseleave', 'fp-nbhd-fill', onNbhdLeave);
        }
      } catch (_) {}
    };
  }, [ready, nbhdVisible, neighborhoodsGeojson, setFilters]);

  // ── Cluster click → zoom in; cursor on hover ─────────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;

    const onClusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER_ID] });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom + 0.5, duration: 400 });
      });
    };
    const onClusterEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const onClusterLeave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click',      CLUSTER_LAYER_ID, onClusterClick);
    map.on('mouseenter', CLUSTER_LAYER_ID, onClusterEnter);
    map.on('mouseleave', CLUSTER_LAYER_ID, onClusterLeave);
    return () => {
      try {
        map.off('click',      CLUSTER_LAYER_ID, onClusterClick);
        map.off('mouseenter', CLUSTER_LAYER_ID, onClusterEnter);
        map.off('mouseleave', CLUSTER_LAYER_ID, onClusterLeave);
      } catch (_) {}
    };
  }, [ready]);

  // ── Individual marker click → popover ────────────────────────────────────────
  // Listens on both IND_LAYER_ID (individual mode) and LAYER_ID (unclustered dots in cluster mode).
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;

    const showPopover = (e) => {
      markerClickedRef.current = true; // flag so map-level click doesn't immediately dismiss
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features?.[0];
      if (!feature) return;
      const clickedId = Number(feature.properties.id);
      const coords    = feature.geometry?.coordinates;
      setHoveredMarker(prev => prev?.id === clickedId ? null : {
        id:       clickedId,
        name:     feature.properties.name,
        imageUrl: feature.properties.image_small_url || null,
        x:        e.point.x,
        y:        e.point.y,
        lng:      coords?.[0] ?? e.lngLat.lng,
        lat:      coords?.[1] ?? e.lngLat.lat,
      });
    };
    const dismissPopover = () => {
      if (poiClickRef.current)    { poiClickRef.current    = false; return; }
      if (markerClickedRef.current) { markerClickedRef.current = false; return; }
      setHoveredMarker(null);
    };
    const onMarkerEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const onMarkerLeave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click',      IND_LAYER_ID, showPopover);
    map.on('click',      LAYER_ID,     showPopover);
    map.on('click',                    dismissPopover);
    map.on('mouseenter', IND_LAYER_ID, onMarkerEnter);
    map.on('mouseleave', IND_LAYER_ID, onMarkerLeave);
    map.on('mouseenter', LAYER_ID,     onMarkerEnter);
    map.on('mouseleave', LAYER_ID,     onMarkerLeave);
    return () => {
      try {
        map.off('click',      IND_LAYER_ID, showPopover);
        map.off('click',      LAYER_ID,     showPopover);
        map.off('click',                    dismissPopover);
        map.off('mouseenter', IND_LAYER_ID, onMarkerEnter);
        map.off('mouseleave', IND_LAYER_ID, onMarkerLeave);
        map.off('mouseenter', LAYER_ID,     onMarkerEnter);
        map.off('mouseleave', LAYER_ID,     onMarkerLeave);
      } catch (_) {}
    };
  }, [ready]);

  // ── Nearest-POI on property hover ────────────────────────────────────────────
  // Draws dashed connectors from the hovered property to the nearest POI of
  // each active category (within 2 km). Distance renders as a bold HTML pill
  // at the line midpoint — far more legible than a map text-symbol on a busy
  // basemap. Disabled when no POIs are toggled on.
  const nearestMarkersRef = useRef([]);
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    const activeKeys = POI_META.filter(m => poi[m.key]).map(m => m.key);

    const clearNearest = () => {
      try { map.getSource(NEAREST_SOURCE_ID)?.setData(EMPTY_GEOJSON); } catch (_) {}
      nearestMarkersRef.current.forEach(m => { try { m.remove(); } catch (_) {} });
      nearestMarkersRef.current = [];
    };

    const renderLabels = (features) => {
      // Clear existing markers first, then mount a new HTML pill per line.
      nearestMarkersRef.current.forEach(m => { try { m.remove(); } catch (_) {} });
      nearestMarkersRef.current = features.map(f => {
        const [a, b]   = f.geometry.coordinates;
        const midpoint = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        const el = document.createElement('div');
        el.textContent = f.properties.label;
        Object.assign(el.style, {
          background:   f.properties.color,
          color:        '#ffffff',
          padding:      '3px 9px',
          borderRadius: '999px',
          fontSize:     '11px',
          fontWeight:   '700',
          letterSpacing: '0.2px',
          lineHeight:   '1.2',
          boxShadow:    '0 2px 6px rgba(0,0,0,0.25), 0 0 0 2px #ffffff',
          whiteSpace:   'nowrap',
          pointerEvents: 'none',
          fontFamily:   'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        });
        return new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat(midpoint)
          .addTo(map);
      });
    };

    if (!activeKeys.length || !filters.cityId) {
      clearNearest();
      return;
    }

    const onEnter = (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const coords = f.geometry?.coordinates || [e.lngLat.lng, e.lngLat.lat];
      const features = computeNearestPoiFeatures(map, coords, activeKeys);
      try {
        map.getSource(NEAREST_SOURCE_ID)?.setData({ type: 'FeatureCollection', features });
      } catch (_) {}
      renderLabels(features);
    };
    const onLeave = () => clearNearest();

    map.on('mouseenter', IND_LAYER_ID, onEnter);
    map.on('mouseenter', LAYER_ID,     onEnter);
    map.on('mouseleave', IND_LAYER_ID, onLeave);
    map.on('mouseleave', LAYER_ID,     onLeave);

    return () => {
      try {
        map.off('mouseenter', IND_LAYER_ID, onEnter);
        map.off('mouseenter', LAYER_ID,     onEnter);
        map.off('mouseleave', IND_LAYER_ID, onLeave);
        map.off('mouseleave', LAYER_ID,     onLeave);
      } catch (_) {}
      clearNearest();
    };
  }, [ready, poi, filters.cityId]);

  // ── Escape key dismisses popover without closing the map panel ────────────────
  useEffect(() => {
    if (!hoveredMarker) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setHoveredMarker(null);
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [hoveredMarker]);

  // ── Keep lngLat ref in sync with current hoveredMarker ───────────────────────
  useEffect(() => {
    hoveredMarkerLngLatRef.current = hoveredMarker
      ? { lng: hoveredMarker.lng, lat: hoveredMarker.lat } : null;
  }, [hoveredMarker]);

  // ── Popover anchor: re-project lngLat → pixels on every map move/zoom ────────
  // Registered once when map is ready; reads lngLat from ref (always current).
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    const update = () => {
      const lngLat = hoveredMarkerLngLatRef.current;
      if (!lngLat) return;
      const pt = map.project([lngLat.lng, lngLat.lat]);
      setHoveredMarker(prev => prev ? { ...prev, x: pt.x, y: pt.y } : null);
    };
    map.on('move', update);
    return () => { try { map.off('move', update); } catch (_) {} };
  }, [ready]);

  // ── Dismiss popover on click outside it (covers non-canvas clicks) ────────────
  // setTimeout(0) skips the click that opened the popover; contains() check
  // prevents closing when clicking inside the popover itself.
  useEffect(() => {
    if (!hoveredMarker) return;
    let active = false;
    const handler = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      setHoveredMarker(null);
    };
    const t = setTimeout(() => {
      active = true;
      document.addEventListener('click', handler);
    }, 0);
    return () => {
      clearTimeout(t);
      if (active) document.removeEventListener('click', handler);
    };
  }, [hoveredMarker?.id]);

  // ── One-time initial fit to all city properties on map load ──────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || hasInitialFitRef.current) return;
    const raw = typeof allGeojson === 'string' ? JSON.parse(allGeojson) : allGeojson;
    const coords = (raw?.features || [])
      .map(f => f?.geometry?.coordinates)
      .filter(c => Array.isArray(c) && c.length >= 2 && isFinite(c[0]) && isFinite(c[1])
        && c[1] >= -90 && c[1] <= 90 && c[0] >= -180 && c[0] <= 180);
    if (!coords.length) return;
    hasInitialFitRef.current = true;
    const lngs = coords.map(c => c[0]);
    const lats  = coords.map(c => c[1]);
    mapRef.current.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 60, maxZoom: 12, duration: 600 },
    );
  }, [ready, allGeojson]);

  // ── Fit to drawn shape boundaries (only when shapes are the sole spatial filter) ──
  useEffect(() => {
    if (!autoZoom) return;
    if (!ready || !mapRef.current || drawnShapes.length === 0) return;
    if (isDraggingRadiusRef.current) return;
    if (editMode || moveMode) return;
    if (filters.neighborhoodIds?.length > 0 || filters.zipCodes?.length > 0) return;
    try {
      const [minLng, minLat, maxLng, maxLat] = turfBbox({ type: 'FeatureCollection', features: drawnShapes });
      mapRef.current.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: { top: 120, bottom: 80, left: 80, right: 100 }, maxZoom: 13, duration: 600 }
      );
    } catch (_) {}
  }, [autoZoom, drawnShapes, ready, editMode, moveMode, filters.neighborhoodIds, filters.zipCodes]);

  // ── Fit to neighborhood polygon boundaries ───────────────────────────────────
  useEffect(() => {
    if (!autoZoom) return;
    if (!ready || !mapRef.current) return;
    if (!filters.neighborhoodIds?.length || !neighborhoodsGeojson) return;
    if (drawnShapes.length > 0) return; // shapes take priority
    if (editMode || moveMode) return;
    const data = typeof neighborhoodsGeojson === 'string'
      ? JSON.parse(neighborhoodsGeojson) : neighborhoodsGeojson;
    const selectedIds = new Set(filters.neighborhoodIds.map(Number));
    const features = (data.features || []).filter(f =>
      selectedIds.has(Number(f.properties?.neighborhood_id ?? f.id ?? f.properties?.id))
    );
    if (!features.length) return;
    try {
      const [minLng, minLat, maxLng, maxLat] = turfBbox({ type: 'FeatureCollection', features });
      mapRef.current.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: { top: 120, bottom: 80, left: 80, right: 100 }, maxZoom: 14, duration: 600 }
      );
    } catch (_) {}
  }, [autoZoom, filters.neighborhoodIds, drawnShapes, neighborhoodsGeojson, ready, editMode, moveMode]);

  // ── Draw mode event listeners ─────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    map.getCanvas().style.cursor = drawMode ? 'crosshair' : '';
    if (!drawMode) return;

    ensureDrawLayers(map);

    // ── Freehand ──────────────────────────────────────────────────────────────
    if (drawMode === 'freehand') {
      const onMouseDown = (e) => {
        isDrawingRef.current = true;
        freehandPointsRef.current = [[e.lngLat.lng, e.lngLat.lat]];
        map.dragPan.disable();
      };
      const onMouseMove = (e) => {
        if (!isDrawingRef.current) return;
        freehandPointsRef.current.push([e.lngLat.lng, e.lngLat.lat]);
        const pts = freehandPointsRef.current;
        if (pts.length >= 2) {
          map.getSource(DRAW_SOURCE_ID)?.setData({
            type: 'Feature', geometry: { type: 'LineString', coordinates: pts },
          });
        }
      };
      const onMouseUp = () => {
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;
        map.dragPan.enable();
        const pts = freehandPointsRef.current;
        if (pts.length >= 3) {
          try {
            const shape = turfPolygon([[...pts, pts[0]]]);
            map.getSource(DRAW_SOURCE_ID)?.setData(shape);
            setDrawnShapes(prev => [...prev, shape]);
            setDrawMode(null);
          } catch (_) {}
        }
        freehandPointsRef.current = [];
      };
      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup',   onMouseUp);
      return () => {
        try {
          map.off('mousedown', onMouseDown);
          map.off('mousemove', onMouseMove);
          map.off('mouseup',   onMouseUp);
          map.dragPan?.enable();
          map.getCanvas().style.cursor = '';
        } catch (_) {}
      };
    }

    // ── Polygon ───────────────────────────────────────────────────────────────
    if (drawMode === 'polygon') {
      const closePolygon = (verts) => {
        try {
          const shape = turfPolygon([[...verts, verts[0]]]);
          map.getSource(DRAW_SOURCE_ID)?.setData(shape);
          map.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
          map.getSource(PREVIEW_SOURCE)?.setData(EMPTY_GEOJSON);
          polygonVerticesRef.current = [];
          setDrawnShapes(prev => [...prev, shape]);
          setDrawMode(null);
        } catch (_) { polygonVerticesRef.current = []; }
      };

      const isNearFirstVertex = (lngLat) => {
        const verts = polygonVerticesRef.current;
        if (verts.length < 3) return false;
        const firstPx = map.project(verts[0]);
        const curPx   = map.project(lngLat);
        const dx = firstPx.x - curPx.x;
        const dy = firstPx.y - curPx.y;
        return Math.sqrt(dx * dx + dy * dy) <= POLYGON_SNAP_PX;
      };

      const onClick = (e) => {
        if (e.originalEvent.detail >= 2) return; // ignore rapid double-tap artifacts
        const pt    = [e.lngLat.lng, e.lngLat.lat];
        const verts = polygonVerticesRef.current;

        // Snap-to-close: clicking near the first vertex closes the polygon
        if (isNearFirstVertex(e.lngLat)) {
          closePolygon(verts);
          return;
        }

        polygonVerticesRef.current.push(pt);
        const newVerts = polygonVerticesRef.current;
        if (newVerts.length >= 2) {
          map.getSource(DRAW_SOURCE_ID)?.setData({
            type: 'Feature', geometry: { type: 'LineString', coordinates: newVerts },
          });
        }
        map.getSource(DRAW_SOURCE_VERTS)?.setData({
          type: 'FeatureCollection',
          features: newVerts.map(c => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c } })),
        });
      };

      // Rubber-band: line from last vertex to cursor; snaps to first vertex when close
      const onMouseMove = (e) => {
        const verts = polygonVerticesRef.current;
        if (verts.length === 0) return;
        const last    = verts[verts.length - 1];
        const snapping = isNearFirstVertex(e.lngLat);
        const cur     = snapping ? verts[0] : [e.lngLat.lng, e.lngLat.lat];
        map.getCanvas().style.cursor = snapping ? 'pointer' : 'crosshair';
        map.getSource(PREVIEW_SOURCE)?.setData({
          type: 'Feature', geometry: { type: 'LineString', coordinates: [last, cur] },
        });
      };

      map.on('click',     onClick);
      map.on('mousemove', onMouseMove);
      return () => {
        try {
          map.off('click',     onClick);
          map.off('mousemove', onMouseMove);
          map.getSource(PREVIEW_SOURCE)?.setData(EMPTY_GEOJSON);
          map.getCanvas().style.cursor = '';
        } catch (_) {}
      };
    }

    // ── Radius (click-drag to resize) ─────────────────────────────────────────
    if (drawMode === 'radius') {
      let isDraggingRadius = false;
      let center = null;
      let currentRadius = 0.5;

      const onMouseDown = (e) => {
        center = [e.lngLat.lng, e.lngLat.lat];
        setRadiusCenter(center);
        isDraggingRadius = true;
        isDraggingRadiusRef.current = true;
        map.dragPan.disable();
      };
      const onMouseMove = (e) => {
        if (!isDraggingRadius || !center) return;
        try {
          const dist = turfDistance(center, [e.lngLat.lng, e.lngLat.lat], { units: 'miles' });
          if (dist >= 0.1) {
            currentRadius = Math.round(dist * 10) / 10;
            setRadiusMiles(currentRadius);
          }
        } catch (_) {}
      };
      const onMouseUp = () => {
        isDraggingRadius = false;
        isDraggingRadiusRef.current = false; // clear before setDrawnShapes so fitBounds effect can fire
        map.dragPan.enable();
        if (center && currentRadius >= 0.1) {
          try {
            const finalShape = turfCircle(center, currentRadius, { units: 'miles', steps: 64 });
            setDrawnShapes(prev => [...prev, finalShape]);
            setDrawMode(null);
          } catch (_) {}
        }
      };

      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup',   onMouseUp);
      return () => {
        try {
          map.off('mousedown', onMouseDown);
          map.off('mousemove', onMouseMove);
          map.off('mouseup',   onMouseUp);
          map.dragPan?.enable();
          map.getCanvas().style.cursor = '';
        } catch (_) {}
      };
    }

    // ── Rectangle ─────────────────────────────────────────────────────────────
    if (drawMode === 'rectangle') {
      let rectStart = null;
      let isRectDrawing = false;

      const onMouseDown = (e) => {
        rectStart = [e.lngLat.lng, e.lngLat.lat];
        isRectDrawing = true;
        map.dragPan.disable();
      };
      const onMouseMove = (e) => {
        if (!isRectDrawing || !rectStart) return;
        const [lng1, lat1] = rectStart;
        const [lng2, lat2] = [e.lngLat.lng, e.lngLat.lat];
        map.getSource(DRAW_SOURCE_ID)?.setData({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[[lng1, lat1], [lng2, lat1], [lng2, lat2], [lng1, lat2], [lng1, lat1]]],
          },
        });
      };
      const onMouseUp = (e) => {
        if (!isRectDrawing || !rectStart) return;
        isRectDrawing = false;
        map.dragPan.enable();
        const [lng1, lat1] = rectStart;
        const [lng2, lat2] = [e.lngLat.lng, e.lngLat.lat];
        rectStart = null;
        if (Math.abs(lng2 - lng1) < 0.001 && Math.abs(lat2 - lat1) < 0.001) return;
        try {
          const coords = [[lng1, lat1], [lng2, lat1], [lng2, lat2], [lng1, lat2], [lng1, lat1]];
          const shape  = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } };
          map.getSource(DRAW_SOURCE_ID)?.setData(shape);
          setDrawnShapes(prev => [...prev, shape]);
          setDrawMode(null);
        } catch (_) {}
      };

      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup',   onMouseUp);
      return () => {
        try {
          map.off('mousedown', onMouseDown);
          map.off('mousemove', onMouseMove);
          map.off('mouseup',   onMouseUp);
          map.dragPan?.enable();
          map.getCanvas().style.cursor = '';
        } catch (_) {}
      };
    }
  }, [drawMode, ready]);

  // ── Radius circle: update on center or miles change ───────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || drawMode !== 'radius' || !radiusCenter) return;
    try {
      ensureDrawLayers(mapRef.current);
      const shape = turfCircle(radiusCenter, radiusMiles, { units: 'miles', steps: 64 });
      mapRef.current.getSource(DRAW_SOURCE_ID)?.setData(shape);
      // Note: shape is committed to drawnShapes[] in onMouseUp, not here (avoids per-frame fitBounds)
      // Radius label on map
      mapRef.current.getSource(RADIUS_LABEL_SOURCE)?.setData({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: radiusCenter },
        properties: { label: `${radiusMiles} mi` },
      });
    } catch (_) {}
  }, [radiusCenter, radiusMiles, drawMode, ready]);

  // Clear radius label when not in radius mode
  useEffect(() => {
    if (!ready || !mapRef.current || drawMode === 'radius') return;
    mapRef.current.getSource(RADIUS_LABEL_SOURCE)?.setData(EMPTY_GEOJSON);
  }, [drawMode, ready]);

  // ── Shape editor mode (selection-aware) ──────────────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || !editMode) return;
    const map = mapRef.current;
    const activeShape = selectedShapeIdx !== null ? drawnShapes[selectedShapeIdx] : null;

    // No shape selected yet — wait for user to click one
    if (!activeShape) {
      map.getCanvas().style.cursor = 'pointer';
      const onClickSelect = (e) => {
        const hits = map.queryRenderedFeatures(e.point, { layers: [SHAPES_FILL_LAYER, SHAPES_LINE_LAYER] });
        if (hits.length > 0) {
          const idx = hits[0].properties?._idx;
          if (idx !== undefined) setSelectedShapeIdx(Number(idx));
        }
        // clicking empty space: stay deselected
      };
      map.on('click', onClickSelect);
      return () => { try { map.off('click', onClickSelect); map.getCanvas().style.cursor = ''; } catch (_) {} };
    }

    // Active shape — show on DRAW_SOURCE_ID + vertex dots
    const coords = activeShape.geometry?.coordinates?.[0] || [];
    editVerticesRef.current = coords.slice(0, -1); // drop closing duplicate
    ensureDrawLayers(map);
    map.getCanvas().style.cursor = 'default';
    map.getSource(DRAW_SOURCE_ID)?.setData(activeShape);
    map.getSource(DRAW_SOURCE_VERTS)?.setData({
      type: 'FeatureCollection',
      features: editVerticesRef.current.map((c, i) => ({
        type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: { idx: i },
      })),
    });

    let isDraggingVertex = false;

    const onMouseDown = (e) => {
      // Vertex drag takes priority
      const vertHits = map.queryRenderedFeatures(e.point, { layers: [DRAW_VERT_ID] });
      if (vertHits.length > 0) {
        const idx = vertHits[0].properties?.idx;
        if (idx !== undefined) {
          draggingVertexIdxRef.current = Number(idx);
          isDraggingVertex = true;
          map.dragPan.disable();
          map.getCanvas().style.cursor = 'grab';
          return;
        }
      }
      // Click on an unselected shape → switch selection
      const shapeHits = map.queryRenderedFeatures(e.point, { layers: [SHAPES_FILL_LAYER, SHAPES_LINE_LAYER] });
      if (shapeHits.length > 0) {
        const idx = shapeHits[0].properties?._idx;
        if (idx !== undefined) { setSelectedShapeIdx(Number(idx)); return; }
      }
      // Empty space → deselect (stay in edit mode)
      setSelectedShapeIdx(null);
    };

    const onMouseMove = (e) => {
      if (!isDraggingVertex || draggingVertexIdxRef.current < 0) return;
      const newCoord = [e.lngLat.lng, e.lngLat.lat];
      editVerticesRef.current[draggingVertexIdxRef.current] = newCoord;
      map.getSource(DRAW_SOURCE_VERTS)?.setData({
        type: 'FeatureCollection',
        features: editVerticesRef.current.map((c, i) => ({
          type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: { idx: i },
        })),
      });
      try {
        const verts = editVerticesRef.current;
        map.getSource(DRAW_SOURCE_ID)?.setData(turfPolygon([[...verts, verts[0]]]));
      } catch (_) {}
    };

    const onMouseUp = () => {
      if (!isDraggingVertex) return;
      isDraggingVertex = false;
      draggingVertexIdxRef.current = -1;
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'default';
      try {
        const verts = editVerticesRef.current;
        if (verts.length >= 3) {
          const shape = turfPolygon([[...verts, verts[0]]]);
          const idx = selectedShapeIdx;
          setDrawnShapes(prev => { const n = [...prev]; n[idx] = shape; return n; });
        }
      } catch (_) {}
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup',   onMouseUp);

    return () => {
      try {
        map.off('mousedown', onMouseDown);
        map.off('mousemove', onMouseMove);
        map.off('mouseup',   onMouseUp);
        map.dragPan?.enable();
        map.getCanvas().style.cursor = '';
        map.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
      } catch (_) {}
    };
  }, [editMode, ready, drawnShapes, selectedShapeIdx]);

  // ── Shape move mode (selection-aware) ────────────────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || !moveMode) return;
    const map = mapRef.current;
    const activeShape = selectedShapeIdx !== null ? drawnShapes[selectedShapeIdx] : null;

    // No shape selected yet — wait for user to click one
    if (!activeShape) {
      map.getCanvas().style.cursor = 'pointer';
      const onClickSelect = (e) => {
        const hits = map.queryRenderedFeatures(e.point, { layers: [SHAPES_FILL_LAYER, SHAPES_LINE_LAYER] });
        if (hits.length > 0) {
          const idx = hits[0].properties?._idx;
          if (idx !== undefined) setSelectedShapeIdx(Number(idx));
        }
        // empty space: stay deselected
      };
      map.on('click', onClickSelect);
      return () => { try { map.off('click', onClickSelect); map.getCanvas().style.cursor = ''; } catch (_) {} };
    }

    // Active shape — show on DRAW_SOURCE_ID for real-time drag updates
    ensureDrawLayers(map);
    map.getSource(DRAW_SOURCE_ID)?.setData(activeShape);
    map.getCanvas().style.cursor = 'grab';

    let isDragging  = false;
    let startLngLat = null;
    let origCoords  = null;

    const onMouseDown = (e) => {
      // Drag the active shape
      const activeHit = map.queryRenderedFeatures(e.point, { layers: [DRAW_FILL_ID, DRAW_LINE_ID] });
      if (activeHit.length > 0) {
        isDragging  = true;
        startLngLat = [e.lngLat.lng, e.lngLat.lat];
        origCoords  = (activeShape.geometry?.coordinates?.[0] || []).slice();
        map.dragPan.disable();
        map.getCanvas().style.cursor = 'grabbing';
        return;
      }
      // Click on another shape → switch selection
      const shapeHits = map.queryRenderedFeatures(e.point, { layers: [SHAPES_FILL_LAYER, SHAPES_LINE_LAYER] });
      if (shapeHits.length > 0) {
        const idx = shapeHits[0].properties?._idx;
        if (idx !== undefined) { setSelectedShapeIdx(Number(idx)); return; }
      }
      // Empty space → deselect
      setSelectedShapeIdx(null);
    };

    const onMouseMove = (e) => {
      if (!isDragging || !startLngLat || !origCoords) return;
      const dLng = e.lngLat.lng - startLngLat[0];
      const dLat = e.lngLat.lat - startLngLat[1];
      const newCoords = origCoords.map(([lng, lat]) => [lng + dLng, lat + dLat]);
      try {
        map.getSource(DRAW_SOURCE_ID)?.setData({
          ...activeShape, geometry: { ...activeShape.geometry, coordinates: [newCoords] },
        });
      } catch (_) {}
    };

    const onMouseUp = (e) => {
      if (!isDragging) return;
      isDragging = false;
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'grab';
      if (!startLngLat || !origCoords) return;
      const dLng = e.lngLat.lng - startLngLat[0];
      const dLat = e.lngLat.lat - startLngLat[1];
      const newCoords = origCoords.map(([lng, lat]) => [lng + dLng, lat + dLat]);
      startLngLat = null; origCoords = null;
      try {
        const shape = { ...activeShape, geometry: { ...activeShape.geometry, coordinates: [newCoords] } };
        const idx = selectedShapeIdx;
        setDrawnShapes(prev => { const n = [...prev]; n[idx] = shape; return n; });
      } catch (_) {}
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup',   onMouseUp);

    return () => {
      try {
        map.off('mousedown', onMouseDown);
        map.off('mousemove', onMouseMove);
        map.off('mouseup',   onMouseUp);
        map.dragPan?.enable();
        map.getCanvas().style.cursor = '';
      } catch (_) {}
    };
  }, [moveMode, ready, drawnShapes, selectedShapeIdx]);

  // ── Draw mode toggle — does NOT clear existing shapes ────────────────────────
  const toggleDrawMode = useCallback((mode) => {
    setEditMode(false);
    setMoveMode(false);
    setSelectedShapeIdx(null);
    setDrawMode(prev => prev === mode ? null : mode);
    setRadiusCenter(null);
    polygonVerticesRef.current = [];
    freehandPointsRef.current  = [];
    isDrawingRef.current = false;
    if (mapRef.current) {
      mapRef.current.dragPan?.enable();
      // Clear in-progress draw sources only; SHAPES_SOURCE_ID keeps finalized shapes
      mapRef.current.getSource(DRAW_SOURCE_ID)?.setData(EMPTY_GEOJSON);
      mapRef.current.getSource(DRAW_SOURCE_VERTS)?.setData(EMPTY_GEOJSON);
      mapRef.current.getSource(PREVIEW_SOURCE)?.setData(EMPTY_GEOJSON);
      mapRef.current.getSource(RADIUS_LABEL_SOURCE)?.setData(EMPTY_GEOJSON);
    }
  }, []);

  // ── Toggle edit mode — auto-selects last shape on enter ──────────────────────
  const toggleEditMode = useCallback(() => {
    setDrawMode(null);
    setMoveMode(false);
    setEditMode(prev => {
      const entering = !prev;
      setSelectedShapeIdx(entering ? drawnShapesRef.current.length - 1 : null);
      return entering;
    });
  }, []);

  // ── Toggle move mode — auto-selects last shape on enter ──────────────────────
  const toggleMoveMode = useCallback(() => {
    setDrawMode(null);
    setEditMode(false);
    setMoveMode(prev => {
      const entering = !prev;
      setSelectedShapeIdx(entering ? drawnShapesRef.current.length - 1 : null);
      return entering;
    });
  }, []);

  // ── Clear dropdowns (neighborhoods + zips only) ───────────────────────────────
  const handleClearDropdowns = useCallback(() => {
    setFilters(f => ({ ...f, neighborhoodIds: [], zipCodes: [], offset: 0 }));
  }, [setFilters]);

  // ── Undo last finalized shape ────────────────────────────────────────────────
  // Single-step undo (no full redo stack). On touch we expose this as the
  // primary recovery affordance since freehand/radius/rectangle aren't
  // available there.
  const handleUndo = useCallback(() => {
    setDrawnShapes(prev => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      setFilters(f => ({ ...f, drawnShape: next, offset: 0 }));
      return next;
    });
    setSelectedShapeIdx(null);
    setEditMode(false);
    setMoveMode(false);
  }, [setFilters]);

  // ── Clear all shapes + modes ──────────────────────────────────────────────────
  const handleClearAll = useCallback(() => {
    // Phone hides the neighborhoods dropdown, so Clear is the only way to
    // drop a selected neighborhood from inside the map there.
    if (isMobile) {
      setFilters(f => ({ ...f, drawnShape: [], neighborhoodIds: [], offset: 0 }));
    } else {
      setFilters(f => ({ ...f, drawnShape: [], offset: 0 }));
    }
    setDrawnShapes([]);
    setSelectedShapeIdx(null);
    setDrawMode(null);
    setEditMode(false);
    setMoveMode(false);
    setRadiusCenter(null);
    polygonVerticesRef.current = [];
    freehandPointsRef.current  = [];
    isDrawingRef.current = false;
    editVerticesRef.current = [];
    if (mapRef.current) {
      mapRef.current.dragPan?.enable();
      mapRef.current.getCanvas().style.cursor = '';
      clearDrawSources(mapRef.current);
    }
  }, [setFilters, isMobile]);

  // ── POI toggle ────────────────────────────────────────────────────────────────
  // Instead of auto-zooming, nudge the user with a brief pill telling them to zoom in.
  const togglePoi = useCallback((key) => {
    if (!filters.cityId) {
      setPoiHint({ visible: true, text: 'Select a city first to see POIs', at: Date.now() });
      return;
    }
    setPoi(prev => {
      const next = { ...prev, [key]: !prev[key] };
      const m = mapRef.current;
      if (m && ready) {
        syncPoiFilter(m, next);
        if (next[key] && m.getZoom() < POI_MIN_ZOOM) {
          setPoiHint({ visible: true, text: 'Zoom in to see POI markers', at: Date.now() });
        }
      }
      return next;
    });
  }, [ready, filters.cityId]);

  const setAllPois = useCallback((on) => {
    if (!filters.cityId) {
      setPoiHint({ visible: true, text: 'Select a city first to see POIs', at: Date.now() });
      return;
    }
    const next = POI_META.reduce((acc, m) => { acc[m.key] = !!on; return acc; }, {});
    setPoi(next);
    const m = mapRef.current;
    if (m && ready) {
      syncPoiFilter(m, next);
      if (on && m.getZoom() < POI_MIN_ZOOM) {
        setPoiHint({ visible: true, text: 'Zoom in to see POI markers', at: Date.now() });
      }
    }
  }, [ready, filters.cityId]);

  // ── Derived select values ─────────────────────────────────────────────────────
  const selectedCity          = cityOptions.find((o) => o.value === filters.cityId) || null;
  const selectedNeighborhoods = neighborhoodOptions.filter((o) => filters.neighborhoodIds.includes(o.value));
  const selectedZips          = zipOptions.filter((o) => filters.zipCodes.includes(o.value));

  const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 99999 }) };

  const closingClass = isClosing ? ' closing' : '';
  const isDrawing    = !!drawMode;
  const hasAnything  = !!(filters.neighborhoodIds?.length || filters.zipCodes?.length || drawnShapes.length || drawMode);

  const content = (
    <>
      {/* Dark backdrop — desktop only. On touch the overlay is full-screen,
          so the backdrop has no reachable area and would only invite the
          DOM-shift-mid-click race that closes the overlay on open. */}
      {!isTouch && (
        <div
          className={`location-expanded-backdrop${closingClass}`}
          onClick={handleClose}
        />
      )}
      {/* Overlay container */}
      <div
        className={`location-expanded-overlay${closingClass}${isTouch ? ' is-touch' : ''}${isMobile ? ' is-mobile' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
        onAnimationEnd={handleAnimationEnd}
      >
        {/* Mobile/tablet top bar — Back + live count */}
        {isTouch && (
          <div className='location-overlay-mobile-topbar'>
            <button
              type='button'
              className='location-overlay-back-btn'
              onClick={handleClose}
              aria-label='Back to all filters'
            >
              <Icon icon='chevron-left' />
              <span>Back</span>
            </button>
            <div className='location-overlay-count'>
              Showing {displayCount.toLocaleString()} {displayCount === 1 ? 'Property' : 'Properties'}
            </div>
          </div>
        )}

        {/* Single-row filter bar — hidden entirely on phone (no dropdowns there) */}
        <div className={`location-expanded-bar${isMobile ? ' is-hidden' : ''}`}>
          <div className='location-expanded-dropdowns'>
            <div className='location-dropdown-col location-dropdown-city'>
              <Select
                options={cityOptions}
                value={selectedCity}
                onChange={(opt) =>
                  setFilters((f) => ({ ...f, cityId: opt.value, neighborhoodIds: [], zipCodes: [], drawnShape: [], offset: 0 }))
                }
                placeholder='City'
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement='bottom'
                classNamePrefix='default-select'
              />
            </div>
            <div className='location-dropdown-col location-dropdown-neighborhoods'>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={neighborhoodOptions}
                value={selectedNeighborhoods}
                onChange={(opts) =>
                  setFilters((f) => ({ ...f, neighborhoodIds: opts.map((o) => o.value), offset: 0 }))
                }
                isDisabled={!filters.cityId}
                placeholder='Neighborhoods'
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement='bottom'
                classNamePrefix='default-select'
              />
            </div>
            <div className='location-dropdown-col location-dropdown-zip'>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={zipOptions}
                value={selectedZips}
                onChange={(opts) =>
                  setFilters((f) => ({ ...f, zipCodes: opts.map((o) => o.value), offset: 0 }))
                }
                isDisabled={!filters.cityId}
                placeholder='ZIP Codes'
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement='bottom'
                classNamePrefix='default-select'
              />
            </div>
          </div>
          <div className='location-expanded-actions'>
            {(filters.neighborhoodIds?.length > 0 || filters.zipCodes?.length > 0) && (
              <button className='location-expanded-clear-btn' onClick={handleClearDropdowns}>
                Clear
              </button>
            )}
            {!isTouch && (
              <Button
                color='primary'
                className='location-expanded-view-btn'
                onClick={handleClose}
              >
                View {displayCount} {displayCount === 1 ? 'Property' : 'Properties'}
              </Button>
            )}
          </div>
        </div>

        {/* Right-side toolbar */}
        <MapToolbar
          drawMode={drawMode}
          onDrawMode={toggleDrawMode}
          shapesCount={drawnShapes.length}
          editMode={editMode}
          onToggleEdit={toggleEditMode}
          moveMode={moveMode}
          onToggleMove={toggleMoveMode}
          markerMode={markerMode}
          onCycleMarkerMode={cycleMarkerMode}
          autoZoom={autoZoom}
          onToggleAutoZoom={() => setAutoZoom(z => !z)}
          nbhdVisible={nbhdVisible}
          onToggleNbhd={() => setNbhdVisible(v => !v)}
          poi={poi}
          onTogglePoi={togglePoi}
          onSetAllPois={setAllPois}
          hasCity={!!filters.cityId}
          onClearAll={handleClearAll}
          onUndo={handleUndo}
          isTouch={isTouch}
        />

        {/* Full-container map */}
        <div ref={containerRef} className='location-expanded-map' />

        {/* POI hint pill — auto-dismisses after 3s */}
        {poiHint.visible && (
          <div
            style={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(17,24,39,0.9)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
              zIndex: 100,
              animation: 'fpPoiHintIn 220ms ease both',
            }}
          >
            {poiHint.text}
          </div>
        )}
        <style>{`@keyframes fpPoiHintIn { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>

        {/* Loading spinner */}
        {loading && (
          <div className='location-map-loading location-map-loading--full'>
            <Icon icon='spinner' spin style={{ fontSize: 28, color: '#999' }} />
          </div>
        )}

        {/* Individual marker hover popover */}
        {hoveredMarker && (() => {
          const isSelected = selectedProperties.some(p => Number(p.propertyId) === hoveredMarker.id);
          return (
            <div
              ref={popoverRef}
              className='marker-popover'
              style={{ left: hoveredMarker.x, top: hoveredMarker.y }}
            >
              <div className='marker-popover__img-wrap'>
                {hoveredMarker.imageUrl
                  ? <img src={hoveredMarker.imageUrl} alt={hoveredMarker.name} className='marker-popover__img' />
                  : <div className='marker-popover__no-img'><Icon icon='building' /></div>
                }
                <button
                  className={`marker-popover__heart${isSelected ? ' is-selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const id = hoveredMarker.id;
                    setSelectedProperties(prev =>
                      prev.some(p => Number(p.propertyId) === id)
                        ? prev.filter(p => Number(p.propertyId) !== id)
                        : [...prev, { propertyId: id }]
                    );
                  }}
                >
                  <Icon icon={isSelected ? 'heart' : ['far', 'heart']} />
                </button>
              </div>
              <div className='marker-popover__name'>{hoveredMarker.name}</div>
            </div>
          );
        })()}
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
}

// ── LocationMapPanel (exported) ───────────────────────────────────────────────
export default function LocationMapPanel({
  filters, setFilters,
  cityOptions, neighborhoodOptions, zipOptions,
  onExpandChange,
  totalMatched = 0,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, loading } = useQuery(PROPERTIES_GEOJSON, {
    variables: buildMapVars(filters),
    skip: !filters.cityId,
    fetchPolicy: 'cache-first',
  });

  const allGeojson = data?.propertiesGeojson || null;

  const { data: nbhdGqlData } = useQuery(NEIGHBORHOODS_GEOJSON, {
    variables: { cityId: filters.cityId },
    skip: !filters.cityId,
    fetchPolicy: 'cache-and-network',
  });
  const neighborhoodsGeojson = nbhdGqlData?.neighborhoodsGeojson ?? null;

  const miniMapGeojson = useMemo(
    () => getDisplayGeojson(allGeojson, filters.neighborhoodIds, filters.zipCodes, filters.drawnShape),
    [allGeojson, filters.neighborhoodIds, filters.zipCodes, filters.drawnShape]
  );

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    onExpandChange?.(true);
  }, [onExpandChange]);

  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
    onExpandChange?.(false);
  }, [onExpandChange]);

  return (
    <>
      <MiniMap
        geojson={miniMapGeojson}
        drawnShape={filters.drawnShape}
        loading={loading}
        onExpand={handleExpand}
        neighborhoodsGeojson={neighborhoodsGeojson}
        selectedNeighborhoodIds={filters.neighborhoodIds}
      />
      {isExpanded && (
        <ExpandedMapOverlay
          allGeojson={allGeojson}
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          cityOptions={cityOptions}
          neighborhoodOptions={neighborhoodOptions}
          zipOptions={zipOptions}
          neighborhoodsGeojson={neighborhoodsGeojson}
          onClose={handleCollapse}
          totalMatched={totalMatched}
        />
      )}
    </>
  );
}
